import { Component, OnInit, OnDestroy } from '@angular/core';
import { Subject } from 'rxjs';
import { takeUntil, debounceTime, distinctUntilChanged } from 'rxjs/operators';
import { MatDialog } from '@angular/material/dialog';
import { NgxSpinnerService } from 'ngx-spinner';
import { ToastrService } from 'ngx-toastr';

import { PopUpDialogService } from 'src/app/services/popup-dialog.service';
import { FeatTiersService } from './feat-tiers.service';
import { TierFeatureDialogData, ModifyTierModalComponent } from './modify-tier-modal/modify-tier-modal.component';
import * as vo from 'src/app/core/model/server/vo/_index';
import { SYSTEM_MAINTAINANCE_TAB_OPTIONS } from 'src/app/lib/constants/tabs.constants';
import { RefreshService } from 'src/app/refresh.service';


@Component({
  selector: 'app-features-by-tiers',
  templateUrl: './features-by-tiers.component.html',
  styleUrl: './features-by-tiers.component.scss'
})
export class FeaturesByTiersComponent implements OnInit, OnDestroy {
  private destroy$ = new Subject<void>();
  private search$  = new Subject<string>();

  tabs = SYSTEM_MAINTAINANCE_TAB_OPTIONS;
  tableSubHeader: any[] = [];
  tierColumns: any[] = [];
  isLoading   = false;
  featureList: vo.TierFeaturesResult[] = [];
  totalItems  = 0;

  constructor(
    private featTiersService: FeatTiersService,
    private toastr: ToastrService,
    private spinner: NgxSpinnerService,
    private popUpDialog: PopUpDialogService,
    private dialog: MatDialog,
    private refreshService: RefreshService,
  ) {}

  ngOnInit(): void {
    this.search$
      .pipe(debounceTime(400), distinctUntilChanged(), takeUntil(this.destroy$))
      .subscribe(() => this.loadFeatures());

    this.refreshService.resetTable$
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => this.loadFeatures())

    this.initTable();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.spinner.hide().catch(console.error);
  }

  // ─── Initialisation ───────────────────────────────────────────────────────

  private initTable(): void {
    this.isLoading = true;
    this.spinner.show().catch(console.error);

    this.featTiersService.getAccountTiers()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res: vo.AccountTiersResponse) => {
          const tiers = res.result ?? [];

          this.tierColumns = tiers.map(t => ({
            uid:      t.uid,
            tierId: t.tierId,
            tierName: t.tierName,
          }));

          this.tableSubHeader = [
            { label: 'FEATURES', sortable: false, centered: true },
            ...this.tierColumns.map((t: any) => ({
              label:    `TIER ${t.tierId} <br>(${t.tierName.toUpperCase()})`,
              sortable: false,
              centered: true,
            })),
            { label: 'ACTIONS', sortable: false, centered: true },
          ];

          this.loadFeatures();
        },
        error: err => {
          this.toastr.error('Failed to load tier configuration. Please try again.');
          this.isLoading = false;
          this.spinner.hide().catch(console.error);
        },
      });
  }

  // ─── Data loading ─────────────────────────────────────────────────────────
  loadFeatures(): void {
    this.isLoading = true;
    this.spinner.show().catch(console.error);

    this.featTiersService
      .getTierFeatures()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: ({ rows, totalItems }) => {
          this.featureList = rows;
          this.totalItems  = totalItems;
        },
        error: err => {
          this.toastr.error('Failed to load tier features. Please try again.');
        },
        complete: () => {
          this.isLoading = false;
          this.spinner.hide().catch(console.error);
        },
      });
  }

  // ─── Cell rendering helper ────────────────────────────────────────────────

  getTierAccess(feature: vo.TierFeaturesResult, tierId: number): string {
    if ((feature.allowedTiers ?? []).includes(tierId)) { return 'Allowed'; }
    if ((feature.deniedTiers  ?? []).includes(tierId)) { return 'Denied';  }
    return 'Denied';
  }

  // ─── Search ───────────────────────────────────────────────────────────────

  onSearchKeyup(event: KeyboardEvent): void {
    this.search$.next((event.target as HTMLInputElement).value);
  }

  // ─── Dialogs ──────────────────────────────────────────────────────────────

  openCreateDialog(): void {
    const dialogRef = this.dialog.open(ModifyTierModalComponent, {
      width: '420px',
      disableClose: true,
      data: { mode: 'create' } satisfies TierFeatureDialogData,
    });
    dialogRef.afterClosed().pipe(takeUntil(this.destroy$))
      .subscribe((saved: boolean) => { if (saved) { this.loadFeatures(); } });
  }

  openEditDialog(feature: vo.TierFeaturesResult): void {
    const dialogRef = this.dialog.open(ModifyTierModalComponent, {
      width: '420px',
      disableClose: true,
      data: { mode: 'edit', feature } satisfies TierFeatureDialogData,
    });
    dialogRef.afterClosed().pipe(takeUntil(this.destroy$))
      .subscribe((saved: boolean) => { if (saved) { this.loadFeatures(); } });
  }

  // ─── Enable / Disable ─────────────────────────────────────────────────────

  toggleStatusConfirmation(feature: vo.TierFeaturesResult): void {
    const enabling = !this.featTiersService.isEnabled(feature.status);
    const action   = enabling ? 'enable' : 'disable';

    this.popUpDialog.openStatusActionDialog(
      enabling
        ? 'Are you sure you want to enable this feature?'
        : 'Are you sure you want to disable this feature?',
      action.toUpperCase(),
      'CANCEL',
      () => this.executeToggle(feature, enabling),
      () => console.log(`${action} cancelled`)
    );
  }

  private executeToggle(feature: vo.TierFeaturesResult, enable: boolean): void {
    this.spinner.show().catch(console.error);

    this.featTiersService
      .updateTierFeature({ featureId: feature.uid, status: enable ? 0 : 1 })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res: any) => {
          if (res.status === 200) {
            this.toastr.success(`Status for "${feature.featureName}" has been successfully updated!`);
            this.loadFeatures();
          } else {
            this.popUpDialog.openErrorDialog(
              `Failed to ${enable ? 'enable' : 'disable'} feature. Please try again later.`
            );
          }
        },
        error: err => {
          this.popUpDialog.openErrorDialog('Something went wrong. Please try again later.');
          this.spinner.hide().catch(console.error);
        },
        complete: () => {
          this.spinner.hide().catch(console.error);
        },
      });
  }

  // ─── Permission guards ────────────────────────────────────────────────────

  getAdminButtonState(): { disabled: boolean; tooltip: string } {
    if (!this.featTiersService.isAdmin) {
      return {
        disabled: true,
        tooltip: 'You are not allowed to change details of tiers. Please contact an Admin if you need assistance',
      };
    }
    return { disabled: false, tooltip: '' };
  }

  // ─── Helpers ──────────────────────────────────────────────────────────────

  getStatusLabel(status: number): string {
    return this.featTiersService.getStatusLabel(status);
  }

  get showingText(): string {
    if (this.totalItems === 0) { return 'No entries found'; }
    return `Showing ${this.totalItems} of ${this.totalItems} entries`;
  }
}
